import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { useCurriculum, useCurriculumCourses, useEnrollCurriculum, useUnenrollCurriculum } from "@/hooks/useCurricula";
import { useCanAccessCurriculum } from "@/hooks/useCurriculumEligibility";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, BookOpen, CheckCircle2, GraduationCap, Clock, Play, Award, UserPlus, LogOut, AlertTriangle, Lock } from "lucide-react";
import { getStorageUrl } from "@/lib/storage";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useCertificates, useCheckAndIssueCertificate, useIsCurriculumComplete, type Certificate } from "@/hooks/useCertificates";
import { useProfile } from "@/hooks/useProfile";
import { CurriculumCompleteDialog } from "@/components/lms/CurriculumCompleteDialog";
import { useHasRole } from "@/hooks/useUserRoles";
import { useAssignCurriculum, useCurriculumAssignments } from "@/hooks/useAssignmentsAdmin";
import { AssignUserDialog } from "@/components/admin/AssignUserDialog";
import { CurriculumProgressPanel } from "@/components/lms/CurriculumProgressPanel";
import { CurriculumProgressSummaryCard } from "@/components/lms/CurriculumProgressSummaryCard";
import { useLearnerCurriculumProgress } from "@/hooks/useLearnerCurriculumProgress";

export default function CurriculumDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: profile } = useProfile();
  const { data: curriculum, isLoading: curriculumLoading } = useCurriculum(id || "");
  const { data: courses, isLoading: coursesLoading } = useCurriculumCourses(id || "");
  const { data: certificates, refetch: refetchCertificates } = useCertificates();
  const { canAccess, isLoading: eligibilityLoading } = useCanAccessCurriculum(id || "");
  const enrollMutation = useEnrollCurriculum();
  const unenrollMutation = useUnenrollCurriculum();
  const checkAndIssueCertificate = useCheckAndIssueCertificate();
  // Authoritative completion check — same source of truth the issuing mutation
  // uses. The old page-level calculation could disagree with it, enabling the
  // button while the mutation returned null and the UI did nothing.
  // MUST stay above the early returns below (rules of hooks).
  const { data: rpcComplete, isLoading: completeLoading } = useIsCurriculumComplete(id);
  
  const [showCertificateDialog, setShowCertificateDialog] = useState(false);
  const [earnedCertificate, setEarnedCertificate] = useState<Certificate | null>(null);
  const [showAssignDialog, setShowAssignDialog] = useState(false);

  const isAdmin = useHasRole("admin");
  const isInstructor = useHasRole("instructor");
  const assignCurriculum = useAssignCurriculum();
  const { data: assignments } = useCurriculumAssignments(id || "");
  // Declared with the other top-level hooks, above the early returns at ~line 104.
  const { data: learnerProgress } = useLearnerCurriculumProgress();
  const progressSummary = learnerProgress?.find((s) => s.curriculumId === id);
  const assignedUserIds = assignments?.map((a) => a.user_id) || [];

  // Find existing certificate for this curriculum
  const existingCertificate = certificates?.find(
    (cert) => cert.curriculum_id === id
  );

  const isCohortOnly = curriculum?.access_mode === 'cohort_only';
  const isCohortEnrolled = curriculum?.is_cohort_enrolled;

  const handleEnroll = async () => {
    if (!user) {
      toast.error("Please sign in to enroll");
      navigate("/auth");
      return;
    }
    if (isCohortOnly && !isCohortEnrolled) {
      toast.error("This curriculum is available by invitation only. Contact your instructor to be enrolled.");
      return;
    }
    if (!canAccess) {
      toast.error("You are not eligible to enroll in this curriculum");
      return;
    }
    if (!id) return;
    try {
      await enrollMutation.mutateAsync(id);
      toast.success("Successfully enrolled in curriculum!");
      // Navigate to the first course in the curriculum if available
      if (courses && courses.length > 0) {
        navigate(`/courses/${courses[0].id}`);
      }
    } catch (error: any) {
      // Check for RLS policy violation (not eligible)
      if (error?.message?.includes("row-level security") || error?.code === "42501") {
        toast.error("This curriculum is available by invitation only. Contact your instructor to be enrolled.");
      } else {
        toast.error("Failed to enroll");
      }
    }
  };

  if (curriculumLoading || coursesLoading || eligibilityLoading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-4 w-96" />
          <div className="grid gap-4">
            {[...Array(3)].map((_, i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!curriculum) {
    return (
      <DashboardLayout>
        <div className="text-center py-16">
          <h2 className="text-xl font-semibold mb-2">Curriculum not found</h2>
          <Button onClick={() => navigate("/curricula")}>Back to Curricula</Button>
        </div>
      </DashboardLayout>
    );
  }

  // Check if user can access (for non-enrolled users)
  const canManage = isAdmin || isInstructor;

  const completedCourses = courses?.filter((c) => c.progress === 100).length || 0;
  const totalCourses = courses?.length || 0;
  const overallProgress = totalCourses > 0 ? Math.round((completedCourses / totalCourses) * 100) : 0;
  const isCurriculumComplete = completeLoading
    ? overallProgress === 100 && totalCourses > 0
    : rpcComplete === true;

  const handleDownloadCertificate = async () => {
    if (!user || !curriculum || !profile) return;
    
    // If certificate exists, show dialog with it
    if (existingCertificate) {
      setEarnedCertificate(existingCertificate);
      setShowCertificateDialog(true);
      return;
    }
    
    // Otherwise, issue a new certificate and show dialog
    try {
      const newCert = await checkAndIssueCertificate.mutateAsync({
        curriculumId: id!,
        curriculumTitle: curriculum.title,
        userName: profile.full_name,
      });
      
      if (newCert) {
        setEarnedCertificate(newCert);
        setShowCertificateDialog(true);
        return;
      }

      // The mutation returns null for TWO different reasons — curriculum not
      // complete, or a certificate already exists. Previously there was no else
      // branch at all, so the button silently did nothing. rpcComplete tells us
      // which case this is without another round trip.
      if (rpcComplete === true) {
        toast.info("You already have a certificate for this curriculum.");
        await refetchCertificates();
      } else {
        toast.error("This curriculum isn't complete yet, so no certificate was issued.");
      }
    } catch (error) {
      toast.error("Failed to issue certificate");
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        {/* Not Eligible Warning */}
        {!canAccess && !curriculum.is_enrolled && !canManage && (
          <Card className="border-destructive bg-destructive/5">
            <CardContent className="py-4">
              <div className="flex items-center gap-3 text-destructive">
                <AlertTriangle className="h-5 w-5" />
                <div>
                  <p className="font-medium">This curriculum is not available to your account</p>
                  <p className="text-sm text-muted-foreground">
                    Based on your role, you cannot enroll in this curriculum. Please explore other learning paths available to you.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Cohort Only Warning */}
        {isCohortOnly && !isCohortEnrolled && !curriculum.is_enrolled && !canManage && (
          <Card className="border-muted">
            <CardContent className="py-4">
              <div className="flex items-center gap-3 text-muted-foreground">
                <Lock className="h-5 w-5" />
                <div>
                  <p className="font-medium text-foreground">Available by invitation only</p>
                  <p className="text-sm">
                    Contact your instructor to be enrolled in this curriculum.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate("/curricula")}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold">{curriculum.title}</h1>
              {curriculum.is_enrolled && (
                <Badge variant="secondary">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Enrolled
                </Badge>
              )}
            </div>
            <p className="text-muted-foreground mt-1">{curriculum.description}</p>
          </div>
          <div className="flex items-center gap-2">
            {(isAdmin || isInstructor) && (
              <Button variant="outline" onClick={() => setShowAssignDialog(true)}>
                <UserPlus className="h-4 w-4 mr-2" />
                Assign Users
              </Button>
            )}
            {curriculum.is_enrolled && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" disabled={unenrollMutation.isPending}>
                    <LogOut className="h-4 w-4 mr-2" />
                    {unenrollMutation.isPending ? "Unenrolling..." : "Unenroll"}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Unenroll from Curriculum?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will remove you from "{curriculum.title}" and all its courses. 
                      Your progress will be lost and cannot be recovered.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={async () => {
                        try {
                          await unenrollMutation.mutateAsync({
                            curriculumId: id!,
                            progress: overallProgress,
                          });
                          toast.success("Successfully unenrolled from curriculum");
                          navigate("/curricula");
                        } catch (error) {
                          toast.error("Failed to unenroll");
                        }
                      }}
                    >
                      Unenroll
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {!curriculum.is_enrolled && isCohortOnly && !isCohortEnrolled && !canManage && (
              <Button disabled variant="outline" className="gap-2">
                <Lock className="h-4 w-4" />
                Invitation Only
              </Button>
            )}
            {!curriculum.is_enrolled && canAccess && !(isCohortOnly && !isCohortEnrolled) && (
              <Button onClick={handleEnroll} disabled={enrollMutation.isPending}>
                {enrollMutation.isPending ? "Enrolling..." : "Enroll in Curriculum"}
              </Button>
            )}
            {!curriculum.is_enrolled && !canAccess && !canManage && !(isCohortOnly && !isCohortEnrolled) && (
              <Button disabled variant="outline">
                Not Available
              </Button>
            )}
          </div>
        </div>
        {/* Progress Overview */}
        {curriculum.is_enrolled && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Your Progress</CardTitle>
            </CardHeader>
            <CardContent>
              {/* Module-level, cohort-aware progress. The previous display
                  counted whole COURSES, which is too coarse for a learner asking
                  "what do I still owe?" — a learner partway through every course
                  showed 0%. Falls back to the course-level bar until the
                  progress RPC has loaded (or if the learner has no modules). */}
              {progressSummary ? (
                <CurriculumProgressSummaryCard
                  summary={progressSummary}
                  showTitle={false}
                />
              ) : (
                <div className="flex items-center gap-4">
                  <Progress value={overallProgress} className="flex-1" />
                  <span className="text-sm font-medium">{overallProgress}%</span>
                </div>
              )}
              <div className="flex items-center justify-between mt-3">
                <p className="text-sm text-muted-foreground">
                  {completedCourses} of {totalCourses} courses completed
                </p>
                <Button
                  onClick={handleDownloadCertificate}
                  disabled={!isCurriculumComplete && !existingCertificate || checkAndIssueCertificate.isPending}
                  variant={(isCurriculumComplete || existingCertificate) ? "default" : "outline"}
                  size="sm"
                >
                  <Award className="h-4 w-4 mr-2" />
                  {checkAndIssueCertificate.isPending 
                    ? "Issuing..." 
                    : existingCertificate 
                      ? "Download Certificate" 
                      : isCurriculumComplete 
                        ? "Get Certificate" 
                        : "Complete all courses for certificate"}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Per-module checklist: which modules are assigned, unlocked, part-done
            or waiting to be marked complete. */}
        {curriculum.is_enrolled && id && (
          <CurriculumProgressPanel
            curriculumId={id}
            showSummary={false}
            title="Modules"
          />
        )}

        {/* Courses List */}
        <div>
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            Courses in this Curriculum
          </h2>
          {courses && courses.length > 0 ? (
            <div className="space-y-4">
              {courses.map((course, index) => {
                const thumbnailUrl = getStorageUrl("course-content", course.thumbnail_url);
                
                return (
                  <Card 
                    key={course.id} 
                    className={`cursor-pointer hover:border-primary/50 transition-colors ${
                      !curriculum.is_enrolled ? "opacity-75" : ""
                    }`}
                    onClick={() => curriculum.is_enrolled && navigate(`/courses/${course.id}`)}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-center gap-4">
                        {/* Course Thumbnail */}
                        <div className="flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden bg-muted shadow-sm">
                          {thumbnailUrl ? (
                            <img
                              src={thumbnailUrl}
                              alt={course.title}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                              <GraduationCap className="h-6 w-6 text-primary/60" />
                            </div>
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <CardTitle className="text-base">{course.title}</CardTitle>
                          <CardDescription className="line-clamp-1">
                            {course.description || "No description"}
                          </CardDescription>
                        </div>
                        {curriculum.is_enrolled && (
                          <div className="flex items-center gap-2">
                            {course.progress === 100 ? (
                              <Badge className="bg-green-600">
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Completed
                              </Badge>
                            ) : course.progress > 0 ? (
                              <Badge variant="secondary">
                                <Clock className="h-3 w-3 mr-1" />
                                {course.progress}% done
                              </Badge>
                            ) : (
                              <Badge variant="outline">
                                <Play className="h-3 w-3 mr-1" />
                                Not started
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    </CardHeader>
                    {curriculum.is_enrolled && course.progress > 0 && course.progress < 100 && (
                      <CardContent className="pt-0">
                        <Progress value={course.progress} className="h-1" />
                      </CardContent>
                    )}
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 bg-muted/50 rounded-lg">
              <GraduationCap className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No courses in this curriculum yet</p>
            </div>
          )}
        </div>
      </div>

      {/* Certificate Dialog */}
      <CurriculumCompleteDialog
        open={showCertificateDialog}
        onOpenChange={setShowCertificateDialog}
        certificate={earnedCertificate}
      />

      {/* Assign Users Dialog */}
      {isAdmin && (
        <AssignUserDialog
          open={showAssignDialog}
          onOpenChange={setShowAssignDialog}
          type="curriculum"
          itemId={id || ""}
          itemTitle={curriculum?.title || ""}
          onAssign={async (userId) => {
            await assignCurriculum.mutateAsync({
              curriculumId: id!,
              userId,
            });
          }}
          assignedUserIds={assignedUserIds}
        />
      )}
    </DashboardLayout>
  );
}
